import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Wrapper } from "../layout/Wrapper";
import { alpha, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";

const tiers = [
  {
    title: "Basic",
    price: "1k",
    description: [
      "Modern Frontend Application",
      "UX/UI Design",
      "Limited functionality",
      "Email Integration",
      "Testing and Deployment",
    ],
    buttonText: "Get a Quote",
    buttonVariant: "outlined",
  },
  {
    title: "Professional",
    subheader: "Recommended",
    price: "5-10K",
    description: [
      "Advanced Software Development",
      "Modern Full Stack Application",
      "User-centric UX/UI Design",
      "Secure robust Backend",
      "Customizable features and advanced functionalities",
      "Third-Party Integration",
      "Comprehensive Testing",
      "Deployment and maintenance Support",
      "Best deals",
    ],
    buttonText: "Get a Quote",
    buttonVariant: "contained",
  },
  {
    title: "Enterprise",
    price: "10K +",
    description: [
      "Enterprise Software Solutions",
      "Collaborating with clients for long-term technology",
      "Scalability and Performance Optimization",
      "Security and Compliance",
      "Dedicated Account Management",
      "Live chat & email support",
    ],
    buttonText: "Get a Quote",
    buttonVariant: "outlined",
  },
];

const PricingPage = ({ mode, toggleColorMode }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <Wrapper mode={mode} toggleColorMode={toggleColorMode}>
      <Box
        sx={(theme) => ({
          width: "100%",
          background:
            theme.palette.mode === "light"
              ? // ? "linear-gradient(180deg, #CEE5FD, #FFF)"
                "#f6f4fa"
              : `linear-gradient(#02294F, ${alpha("#090E10", 0.0)})`,
          backgroundSize: "100% 20%",
          backgroundRepeat: "no-repeat",
        })}
      >
        <Container
          id="pricing"
          sx={{
            pt: { xs: 14, sm: 20 },
            pb: { xs: 8, sm: 16 },
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 3, sm: 6 },
          }}
        >
          <Box
            sx={{
              width: { sm: "100%", md: "60%" },
              textAlign: { sm: "center", md: "center" },
            }}
          >
            <Typography
              component="div"
              variant="body1"
              sx={{
                color:
                  theme.palette.mode === "light"
                    ? theme.palette.secondary.main
                    : "#9043c6",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              PLANS AND PRICING
            </Typography>
            <Typography
              component="h2"
              variant="h4"
              color="text.primary"
              fontWeight="bold"
              textAlign="center"
              sx={{ lineHeight: { xs: "2.5rem" }, mt: 4, mb: 4 }}
            >
              Snyprs software development platform plans
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
            >
              Snyprs has plans to suit individuals and businesses at all levels
              — from solo individual to complex enterprise organizations.
            </Typography>
          </Box>
          <Grid
            data-aos="fade-up"
            data-aos-duration="1000"
            container
            spacing={3}
            alignItems="center"
            justifyContent="center"
          >
            {tiers.map((tier) => (
              <Grid
                item
                key={tier.title}
                xs={12}
                sm={tier.title === "Enterprise" ? 12 : 6}
                md={4}
              >
                <Card
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    border:
                      tier.title === "Professional" ? "1px solid" : undefined,
                    borderColor:
                      tier.title === "Professional"
                        ? "primary.main"
                        : undefined,
                    background:
                      tier.title === "Professional"
                        ? "linear-gradient(#033363, #021F3B)"
                        : undefined,
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        mb: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        color: tier.title === "Professional" ? "grey.100" : "",
                      }}
                    >
                      <Typography component="h3" variant="h6">
                        {tier.title}
                      </Typography>
                      {tier.title === "Professional" && (
                        <Chip
                          icon={<AutoAwesomeIcon />}
                          label={tier.subheader}
                          size="small"
                          sx={{
                            background: (theme) =>
                              theme.palette.mode === "light" ? "" : "none",
                            backgroundColor: "primary.contrastText",
                            "& .MuiChip-label": {
                              color: "primary.dark",
                            },
                            "& .MuiChip-icon": {
                              color: "primary.dark",
                            },
                          }}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        color:
                          tier.title === "Professional" ? "grey.50" : undefined,
                      }}
                    >
                      {/* <Typography component="h3" variant="h2">
                    ${tier.price}
                  </Typography>
                  <Typography component="h3" variant="h6">
                    &nbsp; per project
                  </Typography> */}
                    </Box>
                    <Divider
                      sx={{
                        my: 2,
                        opacity: 0.2,
                        borderColor: "grey.500",
                      }}
                    />
                    {tier.description.map((line) => (
                      <Box
                        key={line}
                        sx={{
                          py: 1,
                          display: "flex",
                          gap: 1.5,
                          alignItems: "center",
                        }}
                      >
                        <CheckCircleRoundedIcon
                          sx={{
                            width: 20,
                            color:
                              tier.title === "Professional"
                                ? "primary.light"
                                : "primary.main",
                          }}
                        />
                        <Typography
                          component="text"
                          variant="subtitle2"
                          sx={{
                            color:
                              tier.title === "Professional"
                                ? "grey.200"
                                : undefined,
                          }}
                        >
                          {line}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                  <CardActions>
                    <Button
                      fullWidth
                      variant={tier.buttonVariant}
                      component="a"
                      onClick={() => navigate("/get-a-quote")}
                    >
                      {tier.buttonText}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Wrapper>
  );
};

export default PricingPage;
